import { GuestCard, GuestCardRequiredFields } from './types'

/**
 * Mapping of predefined guest card fields to their display questions/labels
 * Used for generating guest card forms and displaying field labels
 */
export const GUEST_CARD_FIELD_QUESTIONS: Record<
    keyof Omit<
        GuestCard,
        | 'id'
        | 'card_name'
        | 'archived'
        | 'default'
        | 'apartment'
        | 'header'
        | 'body'
        | 'created_date'
        | 'modified_date'
        | 'card_questions'
        | 'links'
        | 'required_fields'
        | 'default_move_in_date'
        | 'default_move_out_date'
    >,
    string
> = {
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    preferred_unit_config: 'Which floor plan are you interested in?',
    preferred_premiums: 'Are you interested in a premium room?',
    preferred_rent_cost: 'What is your rent budget?',
    preferred_move_in_date: 'What is your preferred move-in date?',
    preferred_move_out_date: 'What is your preferred move-out date?',
    preferred_roommates: 'Would you like to add any preferred roommates?',
    school_attendance: 'What school are you attending?',
    additional_requests: 'Do you have any additional requests or preferences?',
}

export type GuestCardQuestionFieldKey = keyof typeof GUEST_CARD_FIELD_QUESTIONS

export const GUEST_CARD_LOCKED_FIELDS: GuestCardQuestionFieldKey[] = [
    'name',
    'email',
]

export const GUEST_CARD_REMOVABLE_FIELDS: GuestCardQuestionFieldKey[] = [
    'phone',
    'preferred_unit_config',
    'preferred_premiums',
    'preferred_rent_cost',
    'preferred_move_in_date',
    'preferred_move_out_date',
    'preferred_roommates',
    'school_attendance',
    'additional_requests',
]

export const GUEST_CARD_DEFAULT_INCLUDE_FLAGS: Record<
    GuestCardQuestionFieldKey,
    boolean
> = {
    name: true,
    email: true,
    phone: true,
    preferred_unit_config: true,
    preferred_premiums: true,
    preferred_rent_cost: true,
    preferred_move_in_date: true,
    preferred_move_out_date: true,
    preferred_roommates: true,
    school_attendance: true,
    additional_requests: true,
}

export const GUEST_CARD_DEFAULT_REQUIRED_FIELDS: Required<GuestCardRequiredFields> = {
    phone: false,
    preferred_unit_config: true,
    preferred_premiums: true,
    preferred_rent_cost: true,
    preferred_move_in_date: true,
    preferred_move_out_date: true,
    preferred_roommates: true,
    school_attendance: true,
    additional_requests: true,
}

/**
 * Get the question/label for a guest card field
 * @param fieldName - The name of the field
 * @returns The question/label for the field, or the field name if not found
 */
export const getGuestCardFieldQuestion = (
    fieldName: keyof typeof GUEST_CARD_FIELD_QUESTIONS,
): string => {
    return GUEST_CARD_FIELD_QUESTIONS[fieldName] || fieldName
}

export const isGuestCardFieldLocked = (
    field: GuestCardQuestionFieldKey,
): boolean => field === 'name' || field === 'email'

export const isGuestCardFieldRequired = (
    guestCard: Pick<GuestCard, 'required_fields'> | null | undefined,
    field: string,
): boolean => {
    if (field === 'name' || field === 'email') return true
    const stored = guestCard?.required_fields
    if (stored && field in stored) {
        return Boolean(stored[field as keyof GuestCardRequiredFields])
    }
    return false
}

export const requiredFieldsForCreate = (): GuestCardRequiredFields => ({
    ...GUEST_CARD_DEFAULT_REQUIRED_FIELDS,
})

export const requiredFieldsFromCard = (
    card?: Pick<GuestCard, 'required_fields'> | null,
): GuestCardRequiredFields => {
    const resolved: Required<GuestCardRequiredFields> = {
        phone: false,
        preferred_unit_config: false,
        preferred_premiums: false,
        preferred_rent_cost: false,
        preferred_move_in_date: false,
        preferred_move_out_date: false,
        preferred_roommates: false,
        school_attendance: false,
        additional_requests: false,
    }
    const stored = card?.required_fields
    if (stored) {
        const keys = Object.keys(resolved) as Array<
            keyof GuestCardRequiredFields
        >
        keys.forEach((key) => {
            if (key in stored) {
                resolved[key] = Boolean(stored[key])
            }
        })
    }
    return resolved
}

export const guestCardDefaultMoveInDate = (
    data?: {
        default_move_in_date?: string | null
        guest_card?: Pick<GuestCard, 'default_move_in_date'> | null
    } | null,
): string => {
    const raw =
        data?.default_move_in_date ||
        data?.guest_card?.default_move_in_date ||
        ''
    return raw ? raw.slice(0, 10) : ''
}

export const guestCardDefaultMoveOutDate = (
    data?: {
        default_move_out_date?: string | null
        guest_card?: Pick<GuestCard, 'default_move_out_date'> | null
    } | null,
): string => {
    const raw =
        data?.default_move_out_date ||
        data?.guest_card?.default_move_out_date ||
        ''
    return raw ? raw.slice(0, 10) : ''
}
